import { createFileRoute, Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useGuildConfig } from '../../../lib/auditQueries.js';
import { useSpamQueue, useSpamQueueAction, type SpamQueueItem } from '../../../lib/spamQueries.js';
import { Breadcrumbs } from '../../../components/Breadcrumbs.js';
import { LanguageSwitcher } from '../../../components/LanguageSwitcher.js';
import { MemberLabel } from '../../../components/MemberLabel.js';

export const Route = createFileRoute('/guilds/$guildId/spam-queue')({
  component: SpamQueuePage,
});

function SpamQueuePage() {
  const { t } = useTranslation();
  const { guildId } = Route.useParams();
  const queue = useSpamQueue(guildId);
  const act = useSpamQueueAction(guildId);

  const items = queue.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <Header guildId={guildId} />
      <main className="mx-auto max-w-4xl px-6 py-8">
        <p className="mb-6 text-sm text-zinc-400">{t('spamQueue.subtitle')}</p>

        {queue.isLoading ? (
          <p className="text-sm text-zinc-400">{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-8 text-center text-sm text-zinc-500">
            {t('spamQueue.empty')}
          </p>
        ) : (
          <div className="space-y-3">
            {items.map((item) => (
              <QueueCard
                key={item.id}
                item={item}
                guildId={guildId}
                pending={act.isPending}
                onConfirm={() => {
                  act.mutate({ id: item.id, action: 'confirm' });
                }}
                onDismiss={() => {
                  act.mutate({ id: item.id, action: 'dismiss' });
                }}
              />
            ))}
            {queue.hasNextPage ? (
              <button
                type="button"
                onClick={() => {
                  void queue.fetchNextPage();
                }}
                disabled={queue.isFetchingNextPage}
                className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
              >
                {queue.isFetchingNextPage ? t('common.loadingMore') : t('common.loadMore')}
              </button>
            ) : null}
          </div>
        )}
      </main>
    </div>
  );
}

function QueueCard({
  item,
  guildId,
  pending,
  onConfirm,
  onDismiss,
}: {
  item: SpamQueueItem;
  guildId: string;
  pending: boolean;
  onConfirm: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const confidencePct = item.confidence !== null ? Math.round(item.confidence * 100) : null;

  return (
    <div className="flex gap-4 rounded-xl border border-zinc-800 bg-zinc-900/40 p-4">
      <div className="shrink-0">
        {item.kind === 'image' && item.contentUrl ? (
          <img
            src={item.contentUrl}
            alt=""
            className="h-20 w-20 rounded-md border border-zinc-800 object-cover"
          />
        ) : (
          <div className="flex h-20 w-20 items-center justify-center rounded-md border border-zinc-800 bg-zinc-950 text-2xl">
            🔗
          </div>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-xs uppercase tracking-wide text-zinc-300">
            {t(`spamQueue.kind.${item.kind}`)}
          </span>
          {item.category ? (
            <span className="text-xs font-medium text-amber-400">
              {t(`spamQueue.category.${item.category}`, { defaultValue: item.category })}
            </span>
          ) : null}
          {confidencePct !== null ? (
            <span className="text-xs tabular-nums text-zinc-500">{confidencePct}%</span>
          ) : null}
        </div>

        {item.reason ? <p className="mt-1 text-sm text-zinc-300">{item.reason}</p> : null}

        {item.kind === 'link' && item.contentUrl ? (
          <p className="mt-1 truncate font-mono text-xs text-zinc-500">{item.contentUrl}</p>
        ) : null}

        <div className="mt-2 text-xs text-zinc-500">
          <MemberLabel guildId={guildId} userId={item.authorId} compact />
        </div>
      </div>

      <div className="flex shrink-0 flex-col gap-2">
        <button
          type="button"
          onClick={onConfirm}
          disabled={pending}
          className="rounded-md bg-rose-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-rose-500 disabled:opacity-50"
        >
          {t('spamQueue.confirm')}
        </button>
        <button
          type="button"
          onClick={onDismiss}
          disabled={pending}
          className="rounded-md border border-zinc-800 bg-zinc-900 px-3 py-1.5 text-xs text-zinc-300 hover:bg-zinc-800 disabled:opacity-50"
        >
          {t('spamQueue.dismiss')}
        </button>
      </div>
    </div>
  );
}

function Header({ guildId }: { guildId: string }) {
  const { t } = useTranslation();
  const config = useGuildConfig(guildId);
  return (
    <header className="border-b border-zinc-800">
      <div className="mx-auto flex max-w-6xl items-center gap-3 px-6 py-4">
        <Link to="/" className="shrink-0">
          <img src="/icon.png" alt="Frankenstein" className="h-6 w-6" />
        </Link>
        <Breadcrumbs
          items={[
            { label: t('app.name'), to: '/' },
            { label: config.data?.name ?? guildId, to: '/guilds/$guildId', params: { guildId } },
            { label: t('spamQueue.title') },
          ]}
        />
        <div className="ml-auto">
          <LanguageSwitcher />
        </div>
      </div>
    </header>
  );
}
